import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Loader2, X, MailCheck } from "lucide-react";

const inputClasses =
    "w-full bg-white border border-stone-200 focus:border-wine/60 rounded-sm px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-300";

export default function AuthModal() {
    const { user, authModal, closeAuth, completeAuth, login, register } = useAuth();
    const [mode, setMode] = useState("register");
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [resendBusy, setResendBusy] = useState(false);

    const open = authModal !== null;
    const showVerify = authModal === "verify" || (user && !user.email_verified && open && mode === "done");

    useEffect(() => {
        if (authModal === "register" || authModal === "login") {
            setMode(authModal);
            setError("");
        }
    }, [authModal]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [open]);

    const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            if (mode === "forgot") {
                try {
                    await api.post("/auth/forgot-password", { email: form.email });
                } catch {
                    /* never reveal whether the account exists */
                }
                setMode("forgot-sent");
                return;
            }
            if (mode === "register") {
                await register({ ...form, agree_terms: agreeTerms, marketing_consent: marketing });
                setMode("done"); // freshly registered => needs email verification
                toast.success("Account created", { description: "Please verify your email to continue." });
            } else {
                const loggedIn = await login(form.email, form.password);
                if (loggedIn.email_verified) {
                    toast.success("Welcome back", { description: "You are signed in." });
                    completeAuth();
                } else {
                    setMode("done");
                }
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async () => {
        setResendBusy(true);
        try {
            await api.post("/auth/resend-verification");
            toast.success("Verification email sent", { description: "Please check your inbox." });
        } catch (err) {
            toast.error("Could not resend", { description: formatApiError(err) });
        } finally {
            setResendBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="auth-modal"
                    data-testid="auth-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    className="fixed inset-0 z-[96] bg-brand/30 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeAuth();
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-md bg-white border border-stone-200 rounded-sm shadow-[0_24px_80px_rgba(44,58,78,0.15)] px-8 py-10"
                    >
                        <button
                            type="button"
                            aria-label="Close"
                            data-testid="auth-modal-close"
                            onClick={closeAuth}
                            className="absolute top-4 right-4 text-slate-400 hover:text-wine transition-colors duration-300"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {showVerify ? (
                            <div className="text-center" data-testid="auth-verify-step">
                                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-surface2 border border-gold/40">
                                    <MailCheck className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                </span>
                                <h2 className="mt-6 font-serif text-2xl font-light text-brand">Verify your email</h2>
                                <p className="mt-4 text-sm leading-relaxed text-mist">
                                    We’ve sent a verification link to{" "}
                                    <span className="text-brand font-medium">{user?.email}</span>. Please confirm your
                                    email address to complete checkout. Your cart is saved and waiting for you.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={resendBusy}
                                    data-testid="auth-resend-btn"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-wine text-white px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep disabled:opacity-60 transition-colors duration-300"
                                >
                                    {resendBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Resend Verification Email
                                </button>
                                <button
                                    type="button"
                                    onClick={closeAuth}
                                    data-testid="auth-verify-later-btn"
                                    className="mt-4 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-wine transition-colors duration-300"
                                >
                                    Continue Browsing
                                </button>
                            </div>
                        ) : mode === "forgot-sent" ? (
                            <div className="text-center" data-testid="auth-forgot-sent">
                                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-surface2 border border-gold/40">
                                    <MailCheck className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                </span>
                                <h2 className="mt-6 font-serif text-2xl font-light text-brand">Check your inbox</h2>
                                <p className="mt-4 text-sm leading-relaxed text-mist">
                                    If an account exists for <span className="text-brand font-medium">{form.email}</span>,
                                    a secure password-reset link is on its way. The link expires in 1 hour.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => { setMode("login"); setError(""); }}
                                    data-testid="auth-forgot-back-btn"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-wine text-white px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep transition-colors duration-300"
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="eyebrow">Grayhaven Research</p>
                                <h2 className="mt-3 font-serif text-2xl font-light text-brand">
                                    {mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in"}
                                </h2>
                                <p className="mt-2 text-xs leading-relaxed text-mist">
                                    {mode === "register"
                                        ? "Browse and fill your cart freely — an account with a verified email is only needed to place your order."
                                        : mode === "forgot"
                                            ? "Enter your account email address and we'll send you a secure reset link."
                                            : "Sign in to check out — your cart is saved and waiting."}
                                </p>

                                <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="auth-modal-form">
                                    {mode === "register" && (
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" required maxLength={100} placeholder="First Name" value={form.first_name} onChange={update("first_name")} data-testid="modal-first-name-input" className={inputClasses} aria-label="First Name" />
                                            <input type="text" required maxLength={100} placeholder="Last Name" value={form.last_name} onChange={update("last_name")} data-testid="modal-last-name-input" className={inputClasses} aria-label="Last Name" />
                                        </div>
                                    )}
                                    <input type="email" required placeholder="Email Address" value={form.email} onChange={update("email")} data-testid="modal-email-input" className={inputClasses} aria-label="Email Address" />
                                    {mode !== "forgot" && (
                                        <>
                                            <input type="password" required minLength={8} placeholder="Password (min 8 characters)" value={form.password} onChange={update("password")} data-testid="modal-password-input" className={inputClasses} aria-label="Password" />
                                            {mode === "login" && (
                                                <div className="text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setMode("forgot"); setError(""); }}
                                                        data-testid="modal-forgot-password-link"
                                                        className="text-xs font-semibold text-gold hover:text-wine transition-colors duration-300"
                                                    >
                                                        Forgot Password?
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {mode === "register" && (
                                        <div className="space-y-3 pt-1">
                                            <label className="flex items-start gap-3 cursor-pointer" data-testid="modal-terms-check">
                                                <input type="checkbox" required checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#7A2230]" />
                                                <span className="text-[0.7rem] leading-relaxed text-slate-600">
                                                    I agree to the Terms &amp; Conditions and Research Use Policy.
                                                </span>
                                            </label>
                                            <label className="flex items-start gap-3 cursor-pointer" data-testid="modal-marketing-check">
                                                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#7A2230]" />
                                                <span className="text-[0.7rem] leading-relaxed text-slate-600">
                                                    Yes, I’d like to receive Grayhaven Research updates, new-material
                                                    announcements, and other marketing communications by email.{" "}
                                                    <span className="text-slate-400">(Optional)</span>
                                                </span>
                                            </label>
                                        </div>
                                    )}

                                    {error && (
                                        <p data-testid="modal-auth-error" className="text-xs leading-relaxed text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={busy}
                                        data-testid="modal-auth-submit-btn"
                                        className="w-full inline-flex items-center justify-center gap-2 bg-wine text-white px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                                    >
                                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Sign In"}
                                    </button>
                                </form>

                                <p className="mt-6 text-center text-xs text-slate-500">
                                    {mode === "forgot" ? (
                                        <>
                                            Remembered it?{" "}
                                            <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="modal-forgot-back-login" className="text-gold font-semibold hover:text-wine-bright transition-colors duration-300">
                                                Sign In
                                            </button>
                                        </>
                                    ) : mode === "register" ? (
                                        <>
                                            Already have an account?{" "}
                                            <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="modal-switch-login" className="text-gold font-semibold hover:text-wine-bright transition-colors duration-300">
                                                Sign In
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            New to Grayhaven?{" "}
                                            <button type="button" onClick={() => { setMode("register"); setError(""); }} data-testid="modal-switch-register" className="text-gold font-semibold hover:text-wine-bright transition-colors duration-300">
                                                Create Account
                                            </button>
                                        </>
                                    )}
                                </p>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
